<script setup>
import { ref, computed, watch } from "vue";
import { useConsentStatus } from "@/composables/useConsentStatus";

const { isFormDisabled } = useConsentStatus();

const props = defineProps({
  surveyData: {
    type: Object,
    required: true,
  },
  currentStage: Number,
  totalStages: Number,
  nextStageInfo: Object,
});

const emit = defineEmits(["next-stage", "skip-stage"]);

const upskillingSources = [
  "Online courses (Coursera, Udemy, Pluralsight, etc.)",
  "YouTube and free online content",
  "University/College courses",
  "Professional certifications (CISSP, CISM, etc.)",
  "In-house training at current employer",
  "Cybersecurity conferences and events",
  "Professional mentorship",
  "Hands-on lab environments and practice",
  "Industry publications and research",
  "Peer learning and communities",
  "Vendor-specific training",
  "Other (specify)",
];

const certificationCounts = [
  "None",
  "1-2 certifications",
  "3-5 certifications",
  "6-10 certifications",
  "More than 10 certifications",
];

const certificationBodies = [
  "(ISC)² (CISSP, CCSP, etc.)",
  "ISACA (CISA, CISM, etc.)",
  "CompTIA (Security+, CySA+, etc.)",
  "EC-Council (CEH, CISSP, etc.)",
  "SANS/GIAC",
  "Cisco",
  "Microsoft",
  "Amazon Web Services (AWS)",
  "Google Cloud",
  "Vendor-specific (Palo Alto, Fortinet, etc.)",
  "European certifications (EUCIP, etc.)",
  "None",
  "Other (specify)",
];

const handleNext = () => {
  // Validate "Other" fields are filled when selected
  const validationErrors = [];

  // Check upskilling sources
  if (
    props.surveyData.upskillingSources.includes("Other (specify)") &&
    (!props.surveyData.otherUpskillingSources ||
      props.surveyData.otherUpskillingSources.trim() === "")
  ) {
    validationErrors.push(
      'Please specify your upskilling sources when "Other (specify)" is selected.'
    );
  }

  // Check certification bodies
  if (
    props.surveyData.certificationBodies.includes("Other (specify)") &&
    (!props.surveyData.otherCertificationBodies ||
      props.surveyData.otherCertificationBodies.trim() === "")
  ) {
    validationErrors.push(
      'Please specify your certification bodies when "Other (specify)" is selected.'
    );
  }

  if (validationErrors.length > 0) {
    alert(validationErrors.join("\n"));
    return;
  }

  emit("next-stage");
};

// Limit upskilling sources selection to 3
const handleUpskillingChange = (source) => {
  if (props.surveyData.upskillingSources.length > 3) {
    props.surveyData.upskillingSources =
      props.surveyData.upskillingSources.slice(0, 3);
  }
};

// Watch for changes in upskilling sources and clear "Other" field when deselected
watch(
  () => props.surveyData.upskillingSources,
  (newSources, oldSources) => {
    if (
      oldSources &&
      oldSources.includes("Other (specify)") &&
      !newSources.includes("Other (specify)")
    ) {
      props.surveyData.otherUpskillingSources = "";
    }
  }
);

// Watch for changes in certification bodies and clear "Other" field when deselected
watch(
  () => props.surveyData.certificationBodies,
  (newBodies, oldBodies) => {
    if (
      oldBodies &&
      oldBodies.includes("Other (specify)") &&
      !newBodies.includes("Other (specify)")
    ) {
      props.surveyData.otherCertificationBodies = "";
    }
  }
);
</script>

<template>
  <div class="survey-form">
    <img
      src="@/assets/cyberireland_logo.png"
      alt="Cyber Ireland Logo"
      class="logo"
      style="width: 300px; margin: 0 auto; display: block"
    />
    <h2>Career Pathways & Development</h2>

    <!-- Primary Upskilling Sources -->
    <div class="form-group">
      <label
        >What are your primary sources for cybersecurity upskilling and
        professional development? (Select up to 3 most important)</label
      >
      <div class="checkbox-group">
        <label
          v-for="source in upskillingSources"
          :key="source"
          class="checkbox-label"
        >
          <input
            type="checkbox"
            :value="source"
            v-model="surveyData.upskillingSources"
            @change="handleUpskillingChange"
            :disabled="
              isFormDisabled ||
              (surveyData.upskillingSources.length >= 3 &&
                !surveyData.upskillingSources.includes(source))
            "
            :title="`Select up to 3 upskilling sources. Currently selected: ${surveyData.upskillingSources.length}/3`"
          />
          {{ source }}
        </label>
      </div>
      <div v-if="surveyData.upskillingSources.includes('Other (specify)')">
        <input
          type="text"
          id="otherUpskillingSources"
          v-model="surveyData.otherUpskillingSources"
          placeholder="Enter your specific upskilling sources"
          required
          :disabled="isFormDisabled"
        />
      </div>
    </div>

    <!-- Other Upskilling Sources Input (conditional) -->
    <div class="form-group mt-0 mb-0"></div>

    <!-- Certification Status -->
    <div class="form-group">
      <label for="certificationCount"
        >How many active cybersecurity certifications do you currently
        hold?</label
      >
      <select
        id="certificationCount"
        v-model="surveyData.certificationCount"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select number of certifications</option>
        <option
          v-for="count in certificationCounts"
          :key="count"
          :value="count"
        >
          {{ count }}
        </option>
      </select>
    </div>

    <div class="form-group">
      <label
        >Which certification bodies have you obtained certifications from?
        (Select all that apply)</label
      >
      <div class="checkbox-group">
        <label
          v-for="body in certificationBodies"
          :key="body"
          class="checkbox-label"
        >
          <input
            type="checkbox"
            :value="body"
            v-model="surveyData.certificationBodies"
            :disabled="isFormDisabled"
          />
          {{ body }}
        </label>
      </div>
      <div v-if="surveyData.certificationBodies.includes('Other (specify)')">
        <input
          type="text"
          id="otherCertificationBodies"
          v-model="surveyData.otherCertificationBodies"
          placeholder="Enter your specific certification bodies"
          required
          :disabled="isFormDisabled"
        />
      </div>
    </div>

    <!-- Navigation Buttons -->
    <div class="button-group">
      <button
        @click="handleNext"
        class="primary-button"
        :disabled="isFormDisabled"
      >
        Next
      </button>
      <button
        @click="$emit('skip-stage')"
        class="skip-button"
        :disabled="isFormDisabled"
      >
        Skip this section
      </button>
    </div>
  </div>
</template>

<style scoped>
.form-group {
  margin-bottom: 1.5rem;
  width: 100%;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.4;
  text-align: left;
}

.form-group select {
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  cursor: pointer;
}

.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(66, 184, 131, 0.2);
}

.radio-group,
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0px;
  align-items: flex-start;
  width: 100%;
}

.radio-label,
.checkbox-label {
  display: flex !important;
  align-items: flex-start;
  gap: 0.5rem;
  font-weight: normal;
  cursor: pointer;
  width: 100%;
  text-align: left;
  min-height: 44px; /* Touch-friendly height */
  line-height: 1.4;
}

.radio-label input[type="radio"],
.checkbox-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin: 0;
  flex-shrink: 0;
  margin-top: 0.125rem; /* Align with first line of text */
}

.checkbox-label input[type="checkbox"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
  width: 100%;
}

.primary-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 1rem;
}

.primary-button:hover {
  background-color: var(--primary-hover);
}

.skip-button {
  background-color: transparent;
  color: var(--text-color);
  border: 1px solid var(--border-color);
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  width: 100%;
  font-size: 1rem;
}

.skip-button:hover {
  background-color: var(--surface-color);
  border-color: var(--text-color);
}

.survey-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
  width: 100%;
}

.survey-form h2 {
  margin-bottom: 1.5rem;
  color: var(--text-color);
  font-size: 1.5rem;
  font-weight: 600;
  text-align: left;
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-group label {
    font-size: 0.95rem;
  }

  .form-group select {
    padding: 0.75rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .radio-label,
  .checkbox-label {
    min-height: 40px;
    font-size: 0.9rem;
    gap: 0.5rem;
  }

  .radio-label input[type="radio"],
  .checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
  }

  .button-group {
    margin-top: 1.5rem;
    gap: 0.75rem;
  }

  .primary-button,
  .skip-button {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .survey-form {
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2rem; /* Ensure buttons are accessible */
  }

  .survey-form h2 {
    font-size: 1.25rem;
    margin-bottom: 1.25rem;
  }
}

@media (max-width: 480px) {
  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    font-size: 0.9rem;
  }

  .form-group select {
    padding: 0.625rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  .radio-label,
  .checkbox-label {
    min-height: 36px;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  .radio-label input[type="radio"],
  .checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
  }

  .button-group {
    margin-top: 1.25rem;
    gap: 0.625rem;
  }

  .primary-button,
  .skip-button {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  .survey-form {
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2.5rem; /* Ensure buttons are accessible on small screens */
  }

  .survey-form h2 {
    font-size: 1.125rem;
    margin-bottom: 1rem;
  }
}
</style>
